import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Snackbar,
  Typography,
  GlobalStyles,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useLocale } from '@/contexts/LocaleContext'
import { formatDate } from '@/utils/formatDateTime'
import { projectService, type QuestionnaireTemplate } from '@/services/projectService'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import { apiQuestionToEditable, editableToApiQuestion, type ApiQuestion } from '@/components/questions/apiAdapter'
import type { EditableQuestion, QuestionKind } from '@/components/questions/types'

/** The list endpoint already carries each template's questions; the shared type just doesn't say so. */
type LibraryTemplate = QuestionnaireTemplate & {
  questions?: ApiQuestion[]
  is_system?: boolean
  is_own?: boolean
}

type Filter = 'all' | 'mine' | 'team' | 'system'

/** One colour per question kind, reused for the tags and the glyph strip on each row. */
const KIND_COLOR: Record<QuestionKind, string> = {
  text: '#3B6FE0',
  single: '#EA5048',
  multi: '#7A4FD6',
  scale: '#B7791F',
  nps: '#1F8A5B',
  rank: '#13877F',
}

/** Rough answering time per kind, for the "about 1 min" line. */
const KIND_SECONDS: Record<QuestionKind, number> = { text: 25, single: 8, multi: 10, scale: 6, nps: 6, rank: 15 }

const UNDO_MS = 5000

/** Someone else in the caller's team wrote it. */
const fromTeammate = (tpl: LibraryTemplate) => !tpl.is_system && !tpl.is_own

const questionsOf = (tpl: LibraryTemplate): EditableQuestion[] => (tpl.questions ?? []).map(apiQuestionToEditable)

export default function QuestionTemplates() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('questionnaires')
  const { t: tq } = useTranslation('questions')
  const { language } = useLocale()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const [templates, setTemplates] = useState<LibraryTemplate[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [qIndex, setQIndex] = useState(0)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [busy, setBusy] = useState(false)
  // Deletion waits out the undo window before it reaches the API.
  const [pendingDelete, setPendingDelete] = useState<LibraryTemplate | null>(null)
  const deleteTimer = useRef<number | null>(null)

  const load = useCallback(async () => {
    try {
      const data = (await projectService.getQuestionnaireTemplates()) as LibraryTemplate[]
      setTemplates(data)
    } catch (err) {
      setTemplates([])
      setError(err instanceof Error ? err.message : t('loadFailed'))
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    if (!templates) return []
    const needle = query.trim().toLowerCase()
    return templates.filter((tpl) => {
      if (tpl.id === pendingDelete?.id) return false
      if (filter === 'mine' && !tpl.is_own) return false
      if (filter === 'team' && !fromTeammate(tpl)) return false
      if (filter === 'system' && !tpl.is_system) return false
      if (!needle) return true
      return (
        tpl.name.toLowerCase().includes(needle) ||
        (tpl.questions ?? []).some((q) => q.text.toLowerCase().includes(needle))
      )
    })
  }, [templates, query, filter, pendingDelete])

  const mine = visible.filter((tpl) => !tpl.is_system)
  const system = visible.filter((tpl) => tpl.is_system)
  const hasTeam = !!templates?.some(fromTeammate)
  const hasSystem = !!templates?.some((tpl) => tpl.is_system)

  const selectedId = Number(params.get('t')) || null
  const selected = visible.find((tpl) => tpl.id === selectedId) ?? mine[0] ?? system[0] ?? null
  const questions = useMemo(() => (selected ? questionsOf(selected) : []), [selected])

  const select = (id: number) => {
    setParams({ t: String(id) }, { replace: true })
    setQIndex(0)
  }

  // ← → walk the preview through the questions, like turning pages.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return
      if (e.key === 'ArrowRight') setQIndex((i) => Math.min(i + 1, Math.max(questions.length - 1, 0)))
      if (e.key === 'ArrowLeft') setQIndex((i) => Math.max(i - 1, 0))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [questions.length])

  const commitDelete = useCallback(
    async (tpl: LibraryTemplate) => {
      deleteTimer.current = null
      try {
        await projectService.deleteQuestionnaireTemplate(tpl.id)
        setTemplates((list) => list?.filter((x) => x.id !== tpl.id) ?? list)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('deleteFailed'))
      } finally {
        setPendingDelete(null)
      }
    },
    [t],
  )

  const startDelete = (tpl: LibraryTemplate) => {
    setMenuAnchor(null)
    // A second delete inside the window commits the first one straight away.
    if (pendingDelete && deleteTimer.current) {
      window.clearTimeout(deleteTimer.current)
      void commitDelete(pendingDelete)
    }
    setPendingDelete(tpl)
    deleteTimer.current = window.setTimeout(() => void commitDelete(tpl), UNDO_MS)
  }

  const undoDelete = () => {
    if (deleteTimer.current) window.clearTimeout(deleteTimer.current)
    deleteTimer.current = null
    setPendingDelete(null)
  }

  // Leaving the page mid-window still deletes: the user asked for it and never undid it.
  const pendingRef = useRef(pendingDelete)
  pendingRef.current = pendingDelete
  useEffect(
    () => () => {
      if (deleteTimer.current && pendingRef.current) {
        window.clearTimeout(deleteTimer.current)
        void projectService.deleteQuestionnaireTemplate(pendingRef.current.id).catch(() => undefined)
      }
    },
    [],
  )

  const duplicate = async (tpl: LibraryTemplate, thenEdit: boolean) => {
    setBusy(true)
    try {
      const created = await projectService.createQuestionnaireTemplate({
        name: `${tpl.name} ${t('copySuffix')}`.slice(0, 100),
        instructions: tpl.instructions,
        interpretation: tpl.interpretation,
        questions: questionsOf(tpl).map(editableToApiQuestion),
      } as never)
      if (thenEdit) {
        navigate(`/questionnaires/${created.id}/edit`)
        return
      }
      await load()
      setFilter('all')
      select(created.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('duplicateFailed'))
    } finally {
      setBusy(false)
    }
  }

  const loading = templates === null
  const libraryEmpty = !loading && templates.length === 0

  const durationLabel = (qs: EditableQuestion[]) => {
    const s = qs.reduce((sum, q) => sum + KIND_SECONDS[q.kind], 0)
    if (s < 60) return t('durationSec', { value: Math.max(10, Math.round(s / 10) * 10) })
    return t('durationMin', { value: (Math.round(s / 30) / 2).toLocaleString(language) })
  }

  return (
    <ResearcherShell active="templates" crumbs={[t('title')]} hideTopBarAction bleed>
      <GlobalStyles
        styles={{
          '@keyframes fwTplIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
          '@media (prefers-reduced-motion: reduce)': { '.fw-tpl-anim': { animation: 'none !important' } },
        }}
      />
      {loading ? (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : libraryEmpty ? (
        <EmptyLibrary onCreate={() => navigate('/questionnaires/new')} />
      ) : (
        <>
          {/* ── Library ───────────────────────────────────────────── */}
          <Box
            sx={{
              width: { xs: '100%', md: 340 },
              display: { xs: selectedId ? 'none' : 'flex', md: 'flex' },
              flexShrink: 0,
              flexDirection: 'column',
              bgcolor: '#fff',
              borderRight: `1px solid ${c.ink08}`,
              minHeight: 0,
            }}
          >
            <Box sx={{ p: '20px 18px 12px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h1" sx={{ fontSize: 22, letterSpacing: '-0.015em' }}>
                  {t('title')}
                </Typography>
                <IconButton
                  aria-label={t('createTemplate')}
                  onClick={() => navigate('/questionnaires/new')}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '10px',
                    bgcolor: c.coral,
                    color: '#fff',
                    '&:hover': { bgcolor: c.coralHover },
                    '&:active': { transform: 'scale(0.94)' },
                    transition: 'transform 100ms ease-out',
                  }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
              <Box sx={{ height: 36, borderRadius: '10px', bgcolor: c.ink04, display: 'flex', alignItems: 'center', gap: 1, px: 1.25 }}>
                <SearchIcon sx={{ fontSize: 18, color: c.ink48 }} />
                <InputBase
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  sx={{ flex: 1, fontSize: 13.5 }}
                  inputProps={{ 'aria-label': t('searchPlaceholder') }}
                />
              </Box>
              <Segmented
                value={filter}
                onChange={setFilter}
                options={[
                  { value: 'all', label: t('filterAll') },
                  { value: 'mine', label: t('filterMine') },
                  ...(hasTeam ? [{ value: 'team' as Filter, label: t('filterTeam') }] : []),
                  ...(hasSystem ? [{ value: 'system' as Filter, label: t('filterSystem') }] : []),
                ]}
              />
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', pb: 2 }}>
              {visible.length === 0 && (
                <Typography sx={{ px: 2.5, py: 2, fontSize: 13.5, color: c.ink56 }}>{t('noResults', { q: query })}</Typography>
              )}
              {mine.length > 0 && <GroupLabel>{`${t('groupMine')} · ${mine.length}`}</GroupLabel>}
              {mine.map((tpl) => (
                <LibraryRow key={tpl.id} tpl={tpl} selected={tpl.id === selected?.id} onClick={() => select(tpl.id)} />
              ))}
              {system.length > 0 && <GroupLabel>{`${t('groupSystem')} · ${system.length}`}</GroupLabel>}
              {system.map((tpl) => (
                <LibraryRow key={tpl.id} tpl={tpl} selected={tpl.id === selected?.id} onClick={() => select(tpl.id)} />
              ))}
            </Box>
          </Box>

          {/* ── Detail ────────────────────────────────────────────── */}
          {selected && (
            <Box sx={{ flex: 1, minWidth: 0, display: { xs: selectedId ? 'flex' : 'none', md: 'flex' }, flexDirection: 'column', minHeight: 0 }}>
              <Box
                sx={{
                  height: 60,
                  flexShrink: 0,
                  px: { xs: 2, md: 4 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: `1px solid ${c.ink08}`,
                  bgcolor: alpha('#F6F6F8', 0.8),
                  backdropFilter: 'blur(20px) saturate(180%)',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setParams({}, { replace: true })}
                  sx={{ display: { md: 'none' }, mr: 0.5 }}
                  aria-label={t('backToTemplates')}
                >
                  <ChevronLeftIcon />
                </IconButton>
                {selected.is_system ? (
                  <Badge dark>{t('systemBadge')}</Badge>
                ) : fromTeammate(selected) ? (
                  <Badge>{t('teamBadge')}</Badge>
                ) : null}
                <Box sx={{ flex: 1 }} />
                {selected.is_system ? (
                  <Button variant="outlined" size="small" disabled={busy} onClick={() => void duplicate(selected, true)}>
                    {t('duplicateAndEdit')}
                  </Button>
                ) : (
                  <>
                    <IconButton size="small" aria-label={t('more')} onClick={(e) => setMenuAnchor(e.currentTarget)}>
                      <MoreHorizIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Button variant="outlined" size="small" disabled={busy} onClick={() => void duplicate(selected, false)}>
                      {t('duplicate')}
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => navigate(`/questionnaires/${selected.id}/edit`)}>
                      {t('edit')}
                    </Button>
                  </>
                )}
                <Button variant="contained" size="small" onClick={() => navigate(`/studies/new?template=${selected.id}`)}>
                  {t('useInStudy')}
                </Button>
                <Menu
                  anchorEl={menuAnchor}
                  open={!!menuAnchor}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={() => startDelete(selected)} sx={{ color: c.danger, fontSize: 14 }}>
                    {t('delete')}
                  </MenuItem>
                </Menu>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box
                  key={selected.id}
                  className="fw-tpl-anim"
                  sx={{
                    display: 'flex',
                    gap: 5,
                    p: { xs: '20px 16px', md: '28px 32px 40px' },
                    alignItems: 'flex-start',
                    animation: 'fwTplIn 220ms ease-out both',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h1" sx={{ fontSize: { xs: 24, md: 30 }, letterSpacing: '-0.02em', lineHeight: 1.1, mb: 1 }}>
                      {selected.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: 13, color: c.ink56, mb: 2.75 }}>
                      <span>
                        {t('questionsCount', { count: questions.length })} · {durationLabel(questions)}
                      </span>
                      {(selected.updated_at || selected.created_at) && !selected.is_system && (
                        <span>{t('editedOn', { date: formatDate((selected.updated_at || selected.created_at)!, language) })}</span>
                      )}
                    </Box>
                    {selected.instructions && (
                      <Typography sx={{ fontSize: 14, color: c.ink72, lineHeight: 1.55, mb: 2.5, maxWidth: 680 }}>{selected.instructions}</Typography>
                    )}
                    {selected.is_system && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          alignItems: 'center',
                          p: '12px 16px',
                          mb: 2,
                          bgcolor: '#fff',
                          border: `1px solid ${c.ink08}`,
                          borderRadius: '14px',
                          fontSize: 13.5,
                        }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 20, color: c.ink56 }} />
                        {t('systemNote')}
                      </Box>
                    )}
                    {questions.length === 0 && <Typography sx={{ fontSize: 14, color: c.ink56 }}>{t('emptyQuestions')}</Typography>}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {questions.map((q, i) => (
                        <QuestionRow
                          key={q.id}
                          index={i}
                          q={q}
                          active={i === qIndex}
                          kindLabel={tq(`kinds.${q.kind}`)}
                          hint={hintFor(q, t)}
                          onClick={() => setQIndex(i)}
                        />
                      ))}
                    </Box>
                  </Box>

                  {questions.length > 0 && (
                    <Box
                      sx={{
                        width: 290,
                        flexShrink: 0,
                        display: { xs: 'none', lg: 'flex' },
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.75,
                        position: 'sticky',
                        top: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: c.ink56 }}>{t('respondentSees')}</Typography>
                      <PhonePreview q={questions[Math.min(qIndex, questions.length - 1)]} n={Math.min(qIndex, questions.length - 1) + 1} total={questions.length} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" aria-label={t('prevQuestion')} disabled={qIndex === 0} onClick={() => setQIndex((i) => i - 1)}>
                          <ChevronLeftIcon />
                        </IconButton>
                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                          {questions.map((q, i) => (
                            <ButtonBase
                              key={q.id}
                              aria-label={t('questionOf', { n: i + 1, total: questions.length })}
                              onClick={() => setQIndex(i)}
                              sx={{
                                width: i === qIndex ? 20 : 7,
                                height: 7,
                                borderRadius: 4,
                                bgcolor: i === qIndex ? c.coral : c.ink32,
                                transition: 'width 200ms ease-out, background-color 200ms',
                              }}
                            />
                          ))}
                        </Box>
                        <IconButton
                          size="small"
                          aria-label={t('nextQuestion')}
                          disabled={qIndex >= questions.length - 1}
                          onClick={() => setQIndex((i) => i + 1)}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={!!pendingDelete}
        message={pendingDelete ? t('deleted', { name: pendingDelete.name }) : ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <Button size="small" onClick={undoDelete} sx={{ color: '#FF9A93', fontWeight: 700 }}>
            {t('undo')}
          </Button>
        }
        ContentProps={{ sx: { bgcolor: alpha(c.ink, 0.94), backdropFilter: 'blur(20px)', borderRadius: '14px', fontSize: 13.5 } }}
      />
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </ResearcherShell>
  )
}

function hintFor(q: EditableQuestion, t: TFunction<'questionnaires'>): string {
  if (q.kind === 'text') return t('hintOpen')
  if (q.kind === 'scale') return '1–5'
  if (q.kind === 'nps') return '0–10'
  const opts = q.options.map((o) => o.text.trim()).filter(Boolean)
  if (opts.length <= 4) return opts.join(' · ')
  return t('optionsCount', { count: opts.length })
}

function GroupLabel({ children }: { children: string }) {
  const theme = useTheme()
  return (
    <Typography
      sx={{ p: '14px 18px 6px', fontSize: 11, fontWeight: 900, letterSpacing: '.07em', textTransform: 'uppercase', color: theme.affexy.colors.ink48 }}
    >
      {children}
    </Typography>
  )
}

function Badge({ children, dark = false }: { children: string; dark?: boolean }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: 1,
        borderRadius: '6px',
        fontSize: 11.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        bgcolor: dark ? c.ink : c.ink08,
        color: dark ? '#fff' : c.ink72,
      }}
    >
      {children}
    </Box>
  )
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: Array<{ value: T; label: string }>
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box role="tablist" sx={{ display: 'flex', bgcolor: c.ink04, borderRadius: '9px', p: '2px', gap: '2px' }}>
      {options.map((o) => {
        const on = o.value === value
        return (
          <ButtonBase
            key={o.value}
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            sx={{
              flex: 1,
              py: '5px',
              borderRadius: '7px',
              fontSize: 12.5,
              fontWeight: 700,
              fontFamily: 'inherit',
              color: on ? c.ink : c.ink56,
              bgcolor: on ? '#fff' : 'transparent',
              boxShadow: on ? '0 1px 3px rgba(27,28,56,.12)' : 'none',
              transition: 'background-color 150ms, color 150ms',
            }}
          >
            {o.label}
          </ButtonBase>
        )
      })}
    </Box>
  )
}

function LibraryRow({ tpl, selected, onClick }: { tpl: LibraryTemplate; selected: boolean; onClick: () => void }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('questionnaires')
  const { language } = useLocale()
  const qs = questionsOf(tpl)
  const date = tpl.updated_at || tpl.created_at
  return (
    <ButtonBase
      onClick={onClick}
      aria-current={selected ? 'true' : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'left',
        gap: 0.625,
        mx: 1.25,
        my: 0.25,
        px: 1.5,
        py: 1.375,
        width: 'calc(100% - 20px)',
        borderRadius: '12px',
        fontFamily: 'inherit',
        bgcolor: selected ? c.coralSoft : 'transparent',
        transition: 'background-color 120ms, transform 100ms ease-out',
        '&:hover': { bgcolor: selected ? c.coralSoft : c.ink04 },
        '&:active': { transform: 'scale(0.985)' },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tpl.name}
        </Typography>
        {fromTeammate(tpl) && <Badge>{t('teamBadge')}</Badge>}
      </Box>
      <Typography sx={{ fontSize: 12, color: c.ink56 }}>
        {t('questionsCount', { count: tpl.questions_count })}
        {date && !tpl.is_system ? ` · ${t('editedOn', { date: formatDate(date, language) })}` : ''}
      </Typography>
      {qs.length > 0 && (
        <Box sx={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }} aria-hidden>
          {qs.slice(0, 16).map((q) => (
            <Box key={q.id} sx={{ width: 14, height: 4, borderRadius: 2, bgcolor: KIND_COLOR[q.kind], opacity: 0.75 }} />
          ))}
        </Box>
      )}
    </ButtonBase>
  )
}

function QuestionRow({
  index,
  q,
  active,
  kindLabel,
  hint,
  onClick,
}: {
  index: number
  q: EditableQuestion
  active: boolean
  kindLabel: string
  hint: string
  onClick: () => void
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const color = KIND_COLOR[q.kind]
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        textAlign: 'left',
        fontFamily: 'inherit',
        p: '13px 16px',
        bgcolor: '#fff',
        borderRadius: '14px',
        border: `1px solid ${active ? c.coral : c.ink08}`,
        boxShadow: active ? `0 0 0 3px ${alpha(c.coral, 0.12)}` : 'none',
        transition: 'border-color 150ms, box-shadow 150ms',
        '&:hover': { borderColor: active ? c.coral : c.ink32 },
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: c.gray50,
          display: 'grid',
          placeItems: 'center',
          fontSize: 12,
          fontWeight: 700,
          color: c.ink56,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: c.ink, mb: 0.375 }}>{q.text || '—'}</Typography>
        {hint && <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>{hint}</Typography>}
      </Box>
      <Box
        component="span"
        sx={{
          height: 22,
          px: 1,
          borderRadius: '6px',
          fontSize: 11.5,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          color,
          bgcolor: alpha(color, 0.12),
        }}
      >
        {kindLabel}
      </Box>
    </ButtonBase>
  )
}

/** The respondent's phone screen for one question — same order and controls as the real survey. */
function PhonePreview({ q, n, total }: { q: EditableQuestion; n: number; total: number }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('questionnaires')
  const options =
    q.kind === 'scale'
      ? ['1', '2', '3', '4', '5']
      : q.kind === 'nps'
        ? Array.from({ length: 11 }, (_, i) => String(i))
        : q.options.map((o) => o.text.trim()).filter(Boolean)
  const square = q.kind === 'multi' || q.kind === 'rank'

  return (
    <Box
      sx={{
        width: 260,
        height: 524,
        borderRadius: '40px',
        bgcolor: '#111',
        p: '9px',
        boxShadow: '0 24px 60px rgba(27,28,56,.22), 0 2px 6px rgba(27,28,56,.1)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          borderRadius: '31px',
          bgcolor: '#fff',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          p: '38px 18px 18px',
        }}
      >
        <Box sx={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', width: 76, height: 21, borderRadius: 11, bgcolor: '#111' }} />
        <Box sx={{ height: 3, borderRadius: 2, bgcolor: c.ink08, mb: 2.25 }}>
          <Box sx={{ height: '100%', borderRadius: 2, bgcolor: c.coral, width: `${(n / total) * 100}%`, transition: 'width 260ms ease-out' }} />
        </Box>
        <Box key={`${n}-${q.id}`} className="fw-tpl-anim" sx={{ flex: 1, display: 'flex', flexDirection: 'column', animation: 'fwTplIn 200ms ease-out both', minHeight: 0 }}>
          <Typography sx={{ fontSize: 11, color: c.ink56, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', mb: 0.75 }}>
            {t('questionOf', { n, total })}
          </Typography>
          <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 16, lineHeight: 1.3, mb: 2, color: c.ink }}>
            {q.text}
          </Typography>
          {q.kind === 'text' ? (
            <Box sx={{ border: `1.5px solid ${c.ink08}`, borderRadius: '12px', p: 1.5, height: 96, fontSize: 13, color: c.ink48 }}>
              {t('textPlaceholder')}
            </Box>
          ) : q.kind === 'scale' || q.kind === 'nps' ? (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${options.length}, 1fr)`, gap: q.kind === 'nps' ? '3px' : '6px' }}>
                {options.map((o) => (
                  <Box
                    key={o}
                    sx={{ height: q.kind === 'nps' ? 30 : 38, borderRadius: '8px', border: `1.5px solid ${c.ink08}`, display: 'grid', placeItems: 'center', fontSize: q.kind === 'nps' ? 11 : 13 }}
                  >
                    {o}
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: c.ink56, mt: 0.75 }}>
                <span>{t('npsLow')}</span>
                <span>{t('npsHigh')}</span>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
              {options.slice(0, 6).map((o, i) => (
                <Box
                  key={`${o}-${i}`}
                  sx={{ border: `1.5px solid ${c.ink08}`, borderRadius: '12px', p: '10px 12px', fontSize: 13, display: 'flex', gap: 1.125, alignItems: 'center' }}
                >
                  <Box sx={{ width: 16, height: 16, borderRadius: square ? '4px' : '50%', border: `1.5px solid ${c.ink32}`, flexShrink: 0 }} />
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ mt: 'auto', height: 42, borderRadius: '12px', bgcolor: c.coral, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14 }}>
            {t('next')}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function EmptyLibrary({ onCreate }: { onCreate: () => void }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('questionnaires')
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, textAlign: 'center', p: 4 }}>
      <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: '#fff', display: 'grid', placeItems: 'center', boxShadow: theme.affexy.shadows.card }}>
        <FormatListBulletedOutlinedIcon sx={{ fontSize: 26, color: c.ink48 }} />
      </Box>
      <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 22 }}>{t('noTemplatesYet')}</Typography>
      <Typography sx={{ fontSize: 14, color: c.ink56, maxWidth: 420 }}>{t('subtitle')}</Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate} sx={{ mt: 1 }}>
        {t('createFirstTemplate')}
      </Button>
    </Box>
  )
}
